import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  indicatorColors, participantGroupStatusColors,
  roles,
  sortOrderMap, userStatusColors,
  userStatusMap
} from '../../../../../shared/constants/common.constants';
import {CommonService} from '../../../../../shared/services/common.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ParticipantsAndCaregiversList
} from '../../../../../../models/participants-and-caregivers-list';
import {
  ParticipantAndCaregiver
} from '../../../../../../models/participantAndCaregiver';
import {
  AdminGroupListService
} from '../../../services/admin-group-list.service';
import {
  participantsAndCaregiversList
} from '../../../core/admin-groups-list.selectors';
import {
  FetchParticipantsAndCaregiversListRequest
} from '../../../core/admin-groups-list.actions';
import {MatSort, Sort} from "@angular/material/sort";
import {
  profile,
  selectedUserProfile
} from "../../../../../shared/core/shared.selectors";
import {MatTable, MatTableDataSource} from "@angular/material/table";

@Component({
  selector: 'app-participant-or-caregiver-information-table',
  templateUrl: './participant-or-caregiver-information-table.component.html',
  styleUrls: ['./participant-or-caregiver-information-table.component.css']
})
export class ParticipantOrCaregiverInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  loading: boolean = false;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  role: number;
  roles = roles;
  userRole: number;
  sortOrder: any = 1;
  sortColumn: any = 'name';
  isFirstPage = true;
  selectedUserRole: number | null;
  typeMap = {
    4: 'Adult',
    5: 'Participant',
  };
  participantCaregiverList: ParticipantsAndCaregiversList;
  displayedColumns = [
    'name',
    'role',
    'status',
    'phoneNumber',
    'email',
    'gender',
    'preferredPronouns',
    'ageAtStart',
    'mostRecentAttendedSessionsGroup',
    'participantsGroupStatus',
    'mostRecentAttendedSession',
    'totalAttendance',
    'ethnicity',
    'preferredLanguage',
    'race',
    'govtAssist',
    'snap',
    'schoolLunchProgram',
    'foodInsecurity',
    'housingInsecurity',
    'financialStrain',
  ];
  user: any;
  selectedUser: any;
  clientId;
  protected readonly userStatusMap = userStatusMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private participantCaregiverFilters: {
    clientId?: number[],
    groupId?: number[],
    role?: number[],
    status?: number[],
    searchText?: string,
  } = null;

  groupId;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private adminGroupListService: AdminGroupListService,
    private router: Router,
    private route: ActivatedRoute
  ) {

    if (this.route.snapshot.paramMap.get("groupId")) {
      this.groupId = this.route.snapshot.paramMap.get("groupId");
    }

    if (this.route.snapshot.paramMap.get("clientId")) {
      this.clientId = this.route.snapshot.paramMap.get("clientId");
      this.fetchParticipantCaregiverList();
    } else if (this.route.snapshot.paramMap.get("clientAdminId")) {
      this.store.select(profile)
        .pipe(takeUntil(this.OnDestroy))
        .subscribe(data => {
          this.user = data;
        });

      this.store.select(selectedUserProfile)
        .pipe(takeUntil(this.OnDestroy))
        .subscribe(data => {
          if (data.profile) {
            this.selectedUser = data?.profile;
            let clientId = this.user?.clientId;

            if (this.user?.role && this.selectedUser?.role) {
              clientId = this.selectedUser.clientId;
            }
            this.clientId = clientId
            this.fetchParticipantCaregiverList();
          }
        });
    } else {
      this.fetchParticipantCaregiverList();
    }

    this.store.select(participantsAndCaregiversList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.participantCaregiverList = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];
          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }
      });

    // listen for filter changes from the filter component
    this.adminGroupListService.onParticipantCaregiverFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.participantCaregiverFilters = filters;
        this.isFirstPage = true;
        this.fetchParticipantCaregiverList();
      });
  }

  fetchParticipantCaregiverList(pageNumber = 1, limit = 10) {
    if (!this.loading) {
      this.store.dispatch(FetchParticipantsAndCaregiversListRequest({
        payload: {
          ...this.participantCaregiverFilters,
          pageNumber,
          limit,
          ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
          ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
          ...(this.clientId ? {clientId: this.clientId} : {}),
          ...(this.groupId ? {groupId: [this.groupId]} : {})
        }
      }));
    }
  }

  onScroll() {
    if (this.participantCaregiverList?.hasNext && !this.participantCaregiverList?.loading) {
      this.fetchParticipantCaregiverList(this.participantCaregiverList.pageNumber + 1, this.participantCaregiverList.limit);
      this.isFirstPage = false;
    }
  }

  openUserDetails(user: ParticipantAndCaregiver) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/member/${user.id}/behavior-tracker`]);
  }

  groupNameClick(data) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`${urlPrefix}/group/${data.latestGroupId}/dashboard`])
  }

  sessionNameClick(data) {
    const urlPrefix = this.router.url.split('/')[1];
    this.router.navigate([`/${urlPrefix}/group/${data.latestGroupId}/sessions/${data.mostRecentAttendedSession}/details`]);
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];

    this.fetchParticipantCaregiverList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly participantGroupStatusColors = participantGroupStatusColors;
  protected readonly userStatusColors = userStatusColors;
}
